// Minimal re-implementation of Swift's synthesized `Codable` semantics, so the decoders in decode.ts behave like the
// `JSONDecoder` of the iOS app (Foundation JSONDecoder + CoreModels/Support/JSONCoding.swift):
//
// - a non-optional property must be present and non-null (`keyNotFound` / `valueNotFound`);
// - an optional property (`decodeIfPresent`) may be missing or null, but a present value of the wrong type throws;
// - unknown keys are ignored; numbers are not coerced to strings and vice versa; `Int` rejects fractions;
// - string enums reject unknown raw values (`dataCorrupted`);
// - enums with associated values use Swift's shape `{"case": {"_0": v}}` / `{"case": {"label": v}}` / `{"case": {}}`.
//
// Errors carry the coding path, formatted like `DecodingError.compactDescription` in TrailData/TrailDataError.swift
// ("[0].name: missing key 'id'").

import { decodeDateValue, formatDate } from '../core/jsonCoding';
import type { EpochMs } from '../core/models';

export type CodingPathElement = string | number;

export type DecodingErrorKind = 'typeMismatch' | 'valueNotFound' | 'keyNotFound' | 'dataCorrupted';

/** Port of Swift `DecodingError` (the subset the decoders raise). */
export class DecodingError extends Error {
  readonly codingPath: CodingPathElement[];

  constructor(
    readonly kind: DecodingErrorKind,
    readonly detail: string,
    codingPath: CodingPathElement[] = [],
  ) {
    super();
    this.name = 'DecodingError';
    this.codingPath = codingPath;
    this.message = this.compactDescription;
  }

  /** "path: problem", path elements joined by "." with array indices as "[n]" (Swift `compactDescription`). */
  get compactDescription(): string {
    const path = this.codingPath.map((k) => (typeof k === 'number' ? `[${k}]` : k)).join('.');
    return `${path === '' ? '<root>' : path}: ${this.detail}`;
  }

  /** Prepends a container key while the error propagates outwards. */
  prefixed(key: CodingPathElement): this {
    this.codingPath.unshift(key);
    this.message = this.compactDescription;
    return this;
  }
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  return typeof value === 'object' ? 'a dictionary' : `${typeof value}`;
}

function typeMismatch(expected: string, value: unknown): DecodingError {
  return new DecodingError('typeMismatch', `type mismatch (expected ${expected}) – found ${describe(value)} instead`);
}

/** Re-throws `error` with `key` prepended to its coding path (non-decoding errors become `dataCorrupted`). */
export function rethrowAt(error: unknown, key: CodingPathElement): never {
  if (error instanceof DecodingError) throw error.prefixed(key);
  throw new DecodingError('dataCorrupted', error instanceof Error ? error.message : String(error), [key]);
}

/** Options threaded through encoders (Swift encoder `userInfo`). */
export interface EncodeOptions {
  /** Compact polyline/polygon geometry (Swift `JSONCoding.makeEncoder(compactGeometry: true)`). */
  compactGeometry?: boolean;
}

export interface Codec<T> {
  decode(value: unknown): T;
  encode(value: T, options: EncodeOptions): unknown;
}

// MARK: Primitives

export const stringCodec: Codec<string> = {
  decode(v) {
    if (typeof v !== 'string') throw typeMismatch('String', v);
    return v;
  },
  encode: (v) => v,
};

export const doubleCodec: Codec<number> = {
  decode(v) {
    if (typeof v !== 'number') throw typeMismatch('Double', v);
    return v;
  },
  encode: (v) => v,
};

export const intCodec: Codec<number> = {
  decode(v) {
    if (typeof v !== 'number') throw typeMismatch('Int', v);
    if (!Number.isInteger(v)) throw new DecodingError('dataCorrupted', `Parsed JSON number <${v}> does not fit in Int.`);
    return v;
  },
  encode: (v) => v,
};

export const boolCodec: Codec<boolean> = {
  decode(v) {
    if (typeof v !== 'boolean') throw typeMismatch('Bool', v);
    return v;
  },
  encode: (v) => v,
};

/** `Date` with the JSONCoding strategy: ISO 8601 strings or seconds since 1970; encodes ISO 8601 UTC. */
export const dateCodec: Codec<EpochMs> = {
  decode(v) {
    if (typeof v !== 'number' && typeof v !== 'string') throw typeMismatch('Date', v);
    try {
      return decodeDateValue(v);
    } catch (e) {
      throw new DecodingError('dataCorrupted', e instanceof Error ? e.message : String(e));
    }
  },
  encode: (v) => formatDate(v),
};

/** `URL` kept as its string (Swift validates the URL syntax; any string is accepted here). */
export const urlCodec: Codec<string> = stringCodec;

/** String enum with the given raw values (a `Record` so the compiler checks the list is complete). */
export function enumCodec<T extends string>(name: string, rawValues: Record<T, true>): Codec<T> {
  return {
    decode(v) {
      if (typeof v !== 'string') throw typeMismatch(name, v);
      if (!Object.hasOwn(rawValues, v)) {
        throw new DecodingError('dataCorrupted', `Cannot initialize ${name} from invalid String value ${v}`);
      }
      return v as T;
    },
    encode: (v) => v,
  };
}

// MARK: Collections

export function arrayCodec<T>(element: Codec<T>): Codec<T[]> {
  return {
    decode(v) {
      if (!Array.isArray(v)) throw typeMismatch('Array', v);
      const out = new Array<T>(v.length);
      let i = 0;
      try {
        for (; i < v.length; i++) {
          const item = v[i];
          if (item === null || item === undefined) {
            throw new DecodingError('valueNotFound', 'missing value');
          }
          out[i] = element.decode(item);
        }
      } catch (e) {
        rethrowAt(e, i);
      }
      return out;
    },
    encode: (v, options) => v.map((item) => element.encode(item, options)),
  };
}

export function dictionaryCodec<T>(value: Codec<T>): Codec<Record<string, T>> {
  return {
    decode(v) {
      const o = expectObject(v, 'Dictionary');
      const out: Record<string, T> = {};
      for (const key of Object.keys(o)) {
        try {
          out[key] = value.decode(o[key]);
        } catch (e) {
          rethrowAt(e, key);
        }
      }
      return out;
    },
    encode(v, options) {
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(v)) out[key] = value.encode(v[key], options);
      return out;
    },
  };
}

export function expectObject(v: unknown, name = 'a keyed container'): Record<string, unknown> {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) throw typeMismatch(name, v);
  return v as Record<string, unknown>;
}

// MARK: Structs

type FieldMode = 'required' | 'optional' | 'defaulted' | 'lenientOptional' | 'lenientDefaulted';

export interface FieldSpec<T> {
  readonly mode: FieldMode;
  readonly codec: Codec<unknown>;
  readonly fallback?: () => T;
}

/** Non-optional property: `decode(_:forKey:)`. */
export function required<T>(codec: Codec<T>): FieldSpec<T> {
  return { mode: 'required', codec: codec as Codec<unknown> };
}

/** Optional property: `decodeIfPresent(_:forKey:)` (missing or null → undefined; wrong type throws). */
export function optional<T>(codec: Codec<T>): FieldSpec<T | undefined> {
  return { mode: 'optional', codec: codec as Codec<unknown> };
}

/** `decodeIfPresent(_:forKey:) ?? fallback` (missing or null → fallback; wrong type throws). */
export function defaulted<T>(codec: Codec<T>, fallback: () => T): FieldSpec<T> {
  return { mode: 'defaulted', codec: codec as Codec<unknown>, fallback };
}

/** `try? decodeIfPresent(_:forKey:)` (anything undecodable → undefined). */
export function lenientOptional<T>(codec: Codec<T>): FieldSpec<T | undefined> {
  return { mode: 'lenientOptional', codec: codec as Codec<unknown> };
}

/** `(try? decodeIfPresent(_:forKey:)) ?? fallback` (anything undecodable → fallback). */
export function lenientDefaulted<T>(codec: Codec<T>, fallback: () => T): FieldSpec<T> {
  return { mode: 'lenientDefaulted', codec: codec as Codec<unknown>, fallback };
}

/** One spec per property of `T` (every property must be listed). */
export type StructSpec<T> = { [K in keyof T]-?: FieldSpec<T[K]> };

/** Codec of a Swift struct with synthesized (or hand-written lenient) `Codable` conformance. */
export function structCodec<T>(spec: StructSpec<T>): Codec<T> {
  const fields = Object.entries(spec) as Array<[string, FieldSpec<unknown>]>;
  return {
    decode(v) {
      const o = expectObject(v);
      const out: Record<string, unknown> = {};
      let key = '';
      try {
        for (const [name, field] of fields) {
          key = name;
          const raw = o[name];
          switch (field.mode) {
            case 'required':
              if (raw === undefined) {
                key = '';
                throw new DecodingError('keyNotFound', `missing key '${name}'`);
              }
              if (raw === null) throw new DecodingError('valueNotFound', 'missing value');
              out[name] = field.codec.decode(raw);
              break;
            case 'optional':
              if (raw !== undefined && raw !== null) out[name] = field.codec.decode(raw);
              break;
            case 'defaulted':
              out[name] = raw === undefined || raw === null ? field.fallback!() : field.codec.decode(raw);
              break;
            case 'lenientOptional':
              if (raw !== undefined && raw !== null) {
                try {
                  out[name] = field.codec.decode(raw);
                } catch {
                  // `try?`: an undecodable value reads as absent.
                }
              }
              break;
            case 'lenientDefaulted': {
              let value: unknown;
              if (raw !== undefined && raw !== null) {
                try {
                  value = field.codec.decode(raw);
                } catch {
                  value = undefined;
                }
              }
              out[name] = value === undefined ? field.fallback!() : value;
              break;
            }
          }
        }
      } catch (e) {
        if (key === '') throw e;
        rethrowAt(e, key);
      }
      return out as T;
    },
    encode(value, options) {
      const out: Record<string, unknown> = {};
      for (const [name, field] of fields) {
        const v = (value as Record<string, unknown>)[name];
        if (v !== undefined) out[name] = field.codec.encode(v, options);
      }
      return out;
    },
  };
}

// MARK: Enums with associated values

/** Payload field of an enum case: the Swift label (`_0` for an unlabeled value) and its codec. */
export type CasePayload = Record<string, readonly [label: string, codec: Codec<unknown>]>;

/**
 * Codec of a Swift enum with associated values, as the `{ kind: '<case>', …fields }` union `T`. `cases` maps each
 * case name to its payload fields: TS field name → [Swift label, codec].
 */
export function taggedEnumCodec<T extends { kind: string }>(name: string, cases: Record<T['kind'], CasePayload>): Codec<T> {
  const caseNames = Object.keys(cases) as Array<T['kind']>;
  return {
    decode(v) {
      const o = expectObject(v, name);
      const present = caseNames.filter((c) => Object.hasOwn(o, c));
      if (present.length !== 1) {
        throw new DecodingError('typeMismatch', `type mismatch (expected ${name}) – Invalid number of keys found, expected one.`);
      }
      const kind = present[0];
      const out: Record<string, unknown> = { kind };
      try {
        const payload = expectObject(o[kind]);
        const fields = cases[kind];
        for (const field of Object.keys(fields)) {
          const [label, codec] = fields[field];
          const raw = payload[label];
          if (raw === undefined) throw new DecodingError('keyNotFound', `missing key '${label}'`);
          if (raw === null) throw new DecodingError('valueNotFound', 'missing value', [label]);
          try {
            out[field] = codec.decode(raw);
          } catch (e) {
            rethrowAt(e, label);
          }
        }
      } catch (e) {
        rethrowAt(e, kind);
      }
      return out as T;
    },
    encode(value, options) {
      const kind = value.kind as T['kind'];
      const fields = cases[kind];
      const payload: Record<string, unknown> = {};
      for (const field of Object.keys(fields)) {
        const [label, codec] = fields[field];
        payload[label] = codec.encode((value as unknown as Record<string, unknown>)[field], options);
      }
      return { [kind]: payload };
    },
  };
}
